import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getDefaultNotificationFilterTypes } from '../config/notificationTypes';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const [theme, setTheme] = useState('dark');
  const [accentColor, setAccentColor] = useState('blue');
  const [notificationMode, setNotificationMode] = useState('regular');
  const [singleNotifyOnBlockchain, setSingleNotifyOnBlockchain] = useState(false);
  const [layoutMode, setLayoutMode] = useState('sidebar');
  const [maincontentMode, setMaincontentMode] = useState('normal');
  const [communitiesViewMode, setCommunitiesViewMode] = useState('list');
  const [commentDisplayMode, setCommentDisplayMode] = useState('card'); // 'card' | 'inline'
  const [imageDisplayMode, setImageDisplayMode] = useState('inline'); // 'carousel' | 'inline'
  const [showVoteSliderSetting, setShowVoteSliderSetting] = useState(true); // show slider by default
  const [voteDefaultWeight, setVoteDefaultWeight] = useState(100); // percent, default 100%
  const [rewardDisplayMode, setRewardDisplayMode] = useState('default'); // 'default' | 'author_only'
  const [rewardDisplayCurrency, setRewardDisplayCurrency] = useState('hive'); // 'hive' | 'hbd'
  const [signatureFlux, setSignatureFlux] = useState('');
  const [signatureFluxEnabled, setSignatureFluxEnabled] = useState(false);
  const [signatureFlex, setSignatureFlex] = useState('');
  const [signatureFlexEnabled, setSignatureFlexEnabled] = useState(false);
  const [signatureComments, setSignatureComments] = useState('');
  const [signatureCommentsEnabled, setSignatureCommentsEnabled] = useState(true);
  const [showCommunityPinned, setShowCommunityPinned] = useState(false);
  const [defaultBeneficiaries, setDefaultBeneficiaries] = useState([]); // array of "account=weight" strings
  const [startFeed, setStartFeed] = useState('flex'); // 'flex' | 'flux'
  const [startFeedPath, setStartFeedPath] = useState('/feed/flex');
  const [autoloadMore, setAutoloadMore] = useState(true);
  const [autovoteGlobalPause, setAutovoteGlobalPause] = useState(false);
  const [autovoteAdvancedGlobalPause, setAutovoteAdvancedGlobalPause] = useState(false);
  const [autovoteManaLimit, setAutovoteManaLimit] = useState(0);
  const [autovoteAdvancedManaLimit, setAutovoteAdvancedManaLimit] = useState(0);
  const [autovoteAllowSelfVote, setAutovoteAllowSelfVote] = useState(false);
  const [notificationFilterTypes, setNotificationFilterTypes] = useState(getDefaultNotificationFilterTypes());
  const [notificationDisplayVoteWeight, setNotificationDisplayVoteWeight] = useState(false);
  const [notiflexionsEnabled, setNotiflexionsEnabled] = useState(false);
  const [notiflexionsGroupByPostAction, setNotiflexionsGroupByPostAction] = useState(false);
  const [notiflexionsFromListsEnabled, setNotiflexionsFromListsEnabled] = useState(false);
  const [notiflexionsListIds, setNotiflexionsListIds] = useState([]);
  const [notiflexionsListIncludeComments, setNotiflexionsListIncludeComments] = useState([]);
  const [notiflexionsListIncludeShorts, setNotiflexionsListIncludeShorts] = useState([]);
  const [notiflexionsMutedBehaviorEnabled, setNotiflexionsMutedBehaviorEnabled] = useState(false);
  const [notiflexionsMutedBehaviorMode, setNotiflexionsMutedBehaviorMode] = useState('display_muted');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // muted/ignored usernames (lowercase)
  const [mutedUsers, setMutedUsers] = useState([]);

  // Load settings when user changes (fetch server-side settings if available)
  useEffect(() => {
    const load = async () => {
      if (user?.username) {
        // fetch main settings
        let s = {};
        try {
          const resp = await fetch(`/api/user/${encodeURIComponent(user.username)}/settings`);
          if (resp.ok) {
            const data = await resp.json();
            s = data.settings || {};
            console.log(`[SettingsContext] Loaded settings for ${user.username}:`, s);
            setTheme(s.theme || 'dimmed');
            setAccentColor(s.accentColor || 'teal');
            setLayoutMode(s.layoutMode || 'sidebar');
            setMaincontentMode(s.maincontentMode || 'normal');
            setNotificationMode(s.notificationMode || 'regular');
            setSingleNotifyOnBlockchain(typeof s.singleNotifyOnBlockchain === 'boolean' ? s.singleNotifyOnBlockchain : false);
            setCommunitiesViewMode(s.communitiesViewMode || 'list');
            setCommentDisplayMode(s.commentDisplayMode || 'card');
            setImageDisplayMode(s.imageDisplayMode || 'inline');
            setShowVoteSliderSetting(typeof s.showVoteSlider === 'boolean' ? s.showVoteSlider : true);
            setVoteDefaultWeight(typeof s.voteDefaultWeight === 'number' ? s.voteDefaultWeight : 100);
            setRewardDisplayMode(s.rewardDisplayMode || 'default');
            setRewardDisplayCurrency(s.rewardDisplayCurrency || 'hive');
            // Backwards compatibility migration
            const migratedFluxEnabled = typeof s.signatureFluxEnabled === 'boolean' ? s.signatureFluxEnabled : (typeof s.signatureOnArticles === 'boolean' ? s.signatureOnArticles : false);
            const migratedFluxText = s.signatureFlux !== undefined ? s.signatureFlux : (s.signature || '');
            const migratedFlexEnabled = typeof s.signatureFlexEnabled === 'boolean' ? s.signatureFlexEnabled : (typeof s.signatureOnShortPosts === 'boolean' ? s.signatureOnShortPosts : false);
            const migratedFlexText = s.signatureFlex !== undefined ? s.signatureFlex : (s.signature || '');
            const migratedCommentsEnabled = typeof s.signatureCommentsEnabled === 'boolean' ? s.signatureCommentsEnabled : true;
            const migratedCommentsText = s.signatureComments !== undefined ? s.signatureComments : (s.signature || '');

            setSignatureFlux(migratedFluxText);
            setSignatureFluxEnabled(migratedFluxEnabled);
            setSignatureFlex(migratedFlexText);
            setSignatureFlexEnabled(migratedFlexEnabled);
            setSignatureComments(migratedCommentsText);
            setSignatureCommentsEnabled(migratedCommentsEnabled);
            setShowCommunityPinned(typeof s.showCommunityPinned === 'boolean' ? s.showCommunityPinned : false);
            setDefaultBeneficiaries(Array.isArray(s.defaultBeneficiaries) ? s.defaultBeneficiaries : []);
            setStartFeed(s.startFeed || 'flex');
            setStartFeedPath(s.startFeedPath || '/feed/flex');
            setAutoloadMore(s.autoload_more !== false);
            setAutovoteGlobalPause(s.autovote_global_pause === true || s.autovote_global_pause === 'true');
            setAutovoteAdvancedGlobalPause(s.autovote_advanced_global_pause === true || s.autovote_advanced_global_pause === 'true');
            setAutovoteManaLimit(s.autovote_mana_limit ? parseInt(s.autovote_mana_limit, 10) : 0);
            setAutovoteAdvancedManaLimit(s.autovote_advanced_mana_limit ? parseInt(s.autovote_advanced_mana_limit, 10) : 0);
            setAutovoteAllowSelfVote(s.autovote_allow_self_vote === true || s.autovote_allow_self_vote === 'true');
            setNotificationFilterTypes(Array.isArray(s.notificationFilterTypes) ? s.notificationFilterTypes : getDefaultNotificationFilterTypes());
            setNotificationDisplayVoteWeight(typeof s.notificationDisplayVoteWeight === 'boolean' ? s.notificationDisplayVoteWeight : false);
            setNotiflexionsEnabled(typeof s.notiflexionsEnabled === 'boolean' ? s.notiflexionsEnabled : false);
            setNotiflexionsGroupByPostAction(typeof s.notiflexionsGroupByPostAction === 'boolean' ? s.notiflexionsGroupByPostAction : false);
            setNotiflexionsFromListsEnabled(typeof s.notiflexionsFromListsEnabled === 'boolean' ? s.notiflexionsFromListsEnabled : false);
            setNotiflexionsListIds(Array.isArray(s.notiflexionsListIds) ? s.notiflexionsListIds : []);
            setNotiflexionsListIncludeComments(Array.isArray(s.notiflexionsListIncludeComments) ? s.notiflexionsListIncludeComments : []);
            setNotiflexionsListIncludeShorts(Array.isArray(s.notiflexionsListIncludeShorts) ? s.notiflexionsListIncludeShorts : []);
            setNotiflexionsMutedBehaviorEnabled(typeof s.notiflexionsMutedBehaviorEnabled === 'boolean' ? s.notiflexionsMutedBehaviorEnabled : false);
            setNotiflexionsMutedBehaviorMode(typeof s.notiflexionsMutedBehaviorMode === 'string' ? s.notiflexionsMutedBehaviorMode : 'display_muted');
          } else {
            console.warn(`[SettingsContext] Server returned ${resp.status}, falling back to localStorage`);
            // Fallback to localStorage when server not available
            const savedTheme = localStorage.getItem(`app-theme-${user.username}`);
            const savedAccent = localStorage.getItem(`app-accent-${user.username}`);
            const savedLayout = localStorage.getItem(`app-layout-${user.username}`);
            const savedMaincontent = localStorage.getItem(`app-maincontent-${user.username}`);
            const savedCommunitiesView = localStorage.getItem(`app-communities-view-${user.username}`);
            setTheme(savedTheme || 'dimmed');
            setAccentColor(savedAccent || 'teal');
            setLayoutMode(savedLayout || 'sidebar');
            setMaincontentMode(savedMaincontent || 'normal');
            setNotificationMode('regular');
            setSingleNotifyOnBlockchain(localStorage.getItem(`app-single-notify-chain-${user.username}`) === 'true');
            setCommunitiesViewMode(savedCommunitiesView || 'list');
            const savedCommentDisplay = localStorage.getItem(`app-comment-display-${user.username}`) || 'card';
            setCommentDisplayMode(savedCommentDisplay);
            const savedImageDisplay = localStorage.getItem(`app-image-display-${user.username}`) || 'inline';
            setImageDisplayMode(savedImageDisplay);
            const savedShowSlider = localStorage.getItem(`app-show-vote-slider-${user.username}`);
            setShowVoteSliderSetting(savedShowSlider === null ? true : savedShowSlider === 'true');
            const savedVoteWeight = localStorage.getItem(`app-vote-default-weight-${user.username}`);
            setVoteDefaultWeight(savedVoteWeight ? parseInt(savedVoteWeight, 10) : 100);
            const savedRewardDisplay = localStorage.getItem(`app-reward-display-${user.username}`) || 'default';
            setRewardDisplayMode(savedRewardDisplay);
            const savedRewardCurrency = localStorage.getItem(`app-reward-currency-${user.username}`) || 'hive';
            setRewardDisplayCurrency(savedRewardCurrency);
            const savedFluxEnabled = localStorage.getItem(`app-signature-flux-enabled-${user.username}`);
            const savedFluxText = localStorage.getItem(`app-signature-flux-${user.username}`);
            const savedFlexEnabled = localStorage.getItem(`app-signature-flex-enabled-${user.username}`);
            const savedFlexText = localStorage.getItem(`app-signature-flex-${user.username}`);
            const savedCommentsEnabled = localStorage.getItem(`app-signature-comments-enabled-${user.username}`);
            const savedCommentsText = localStorage.getItem(`app-signature-comments-${user.username}`);

            // If not found in localStorage, fallback to old keys or defaults
            const legacySignature = localStorage.getItem(`app-signature-${user.username}`) || '';
            const legacyShortPosts = localStorage.getItem(`app-signature-short-posts-${user.username}`) === 'true';
            const legacyArticles = localStorage.getItem(`app-signature-articles-${user.username}`) === 'true';

            setSignatureFluxEnabled(savedFluxEnabled !== null ? savedFluxEnabled === 'true' : legacyArticles);
            setSignatureFlux(savedFluxText !== null ? savedFluxText : legacySignature);
            setSignatureFlexEnabled(savedFlexEnabled !== null ? savedFlexEnabled === 'true' : legacyShortPosts);
            setSignatureFlex(savedFlexText !== null ? savedFlexText : legacySignature);
            setSignatureCommentsEnabled(savedCommentsEnabled !== null ? savedCommentsEnabled === 'true' : true);
            setSignatureComments(savedCommentsText !== null ? savedCommentsText : legacySignature);
            const savedShowCommunityPinned = localStorage.getItem(`app-show-community-pinned-${user.username}`);
            setShowCommunityPinned(savedShowCommunityPinned === 'true');
            const savedAutoloadMore = localStorage.getItem(`app-autoload-more-${user.username}`);
            setAutoloadMore(savedAutoloadMore !== 'false');
            const savedNotificationFilterTypes = localStorage.getItem(`app-notification-filter-types-${user.username}`);
            setNotificationFilterTypes(savedNotificationFilterTypes ? JSON.parse(savedNotificationFilterTypes) : getDefaultNotificationFilterTypes());
            setNotificationDisplayVoteWeight(localStorage.getItem(`app-notification-display-vote-weight-${user.username}`) === 'true');
            setNotiflexionsEnabled(localStorage.getItem(`app-notiflexions-enabled-${user.username}`) === 'true');
            setNotiflexionsGroupByPostAction(localStorage.getItem(`app-notiflexions-group-by-post-action-${user.username}`) === 'true');
            setNotiflexionsFromListsEnabled(localStorage.getItem(`app-notiflexions-from-lists-enabled-${user.username}`) === 'true');
            const savedNotiflexionsListIds = localStorage.getItem(`app-notiflexions-list-ids-${user.username}`);
            setNotiflexionsListIds(savedNotiflexionsListIds ? JSON.parse(savedNotiflexionsListIds) : []);
            const savedNotiflexionsListIncludeComments = localStorage.getItem(`app-notiflexions-list-include-comments-${user.username}`);
            setNotiflexionsListIncludeComments(savedNotiflexionsListIncludeComments ? JSON.parse(savedNotiflexionsListIncludeComments) : []);
            const savedNotiflexionsListIncludeShorts = localStorage.getItem(`app-notiflexions-list-include-shorts-${user.username}`);
            setNotiflexionsListIncludeShorts(savedNotiflexionsListIncludeShorts ? JSON.parse(savedNotiflexionsListIncludeShorts) : []);
            setNotiflexionsMutedBehaviorEnabled(localStorage.getItem(`app-notiflexions-muted-behavior-enabled-${user.username}`) === 'true');
            setNotiflexionsMutedBehaviorMode(localStorage.getItem(`app-notiflexions-muted-behavior-mode-${user.username}`) || 'display_muted');
          }
        } catch (err) {
          console.error(`[SettingsContext] Error loading settings:`, err);
          const savedTheme = localStorage.getItem(`app-theme-${user.username}`);
          const savedAccent = localStorage.getItem(`app-accent-${user.username}`);
          const savedLayout = localStorage.getItem(`app-layout-${user.username}`);
          const savedMaincontent = localStorage.getItem(`app-maincontent-${user.username}`);
          const savedCommunitiesView = localStorage.getItem(`app-communities-view-${user.username}`);
          setTheme(savedTheme || 'dimmed');
          setAccentColor(savedAccent || 'teal');
          setLayoutMode(savedLayout || 'sidebar');
          setMaincontentMode(savedMaincontent || 'normal');
          setNotificationMode('regular');
          setSingleNotifyOnBlockchain(localStorage.getItem(`app-single-notify-chain-${user.username}`) === 'true');
          setCommunitiesViewMode(savedCommunitiesView || 'list');
          const savedCommentDisplay = localStorage.getItem(`app-comment-display-${user.username}`) || 'card';
          setCommentDisplayMode(savedCommentDisplay);
          const savedImageDisplay = localStorage.getItem(`app-image-display-${user.username}`) || 'inline';
          setImageDisplayMode(savedImageDisplay);
          const savedShowSlider = localStorage.getItem(`app-show-vote-slider-${user.username}`);
          setShowVoteSliderSetting(savedShowSlider === null ? true : savedShowSlider === 'true');
          const savedVoteWeight = localStorage.getItem(`app-vote-default-weight-${user.username}`);
          setVoteDefaultWeight(savedVoteWeight ? parseInt(savedVoteWeight, 10) : 100);
          const savedRewardDisplay = localStorage.getItem(`app-reward-display-${user.username}`) || 'default';
          setRewardDisplayMode(savedRewardDisplay);
          const savedRewardCurrency = localStorage.getItem(`app-reward-currency-${user.username}`) || 'hive';
          setRewardDisplayCurrency(savedRewardCurrency);
          const savedFluxEnabled = localStorage.getItem(`app-signature-flux-enabled-${user.username}`);
          const savedFluxText = localStorage.getItem(`app-signature-flux-${user.username}`);
          const savedFlexEnabled = localStorage.getItem(`app-signature-flex-enabled-${user.username}`);
          const savedFlexText = localStorage.getItem(`app-signature-flex-${user.username}`);
          const savedCommentsEnabled = localStorage.getItem(`app-signature-comments-enabled-${user.username}`);
          const savedCommentsText = localStorage.getItem(`app-signature-comments-${user.username}`);

          const legacySignature = localStorage.getItem(`app-signature-${user.username}`) || '';
          const legacyShortPosts = localStorage.getItem(`app-signature-short-posts-${user.username}`) === 'true';
          const legacyArticles = localStorage.getItem(`app-signature-articles-${user.username}`) === 'true';

          setSignatureFluxEnabled(savedFluxEnabled !== null ? savedFluxEnabled === 'true' : legacyArticles);
          setSignatureFlux(savedFluxText !== null ? savedFluxText : legacySignature);
          setSignatureFlexEnabled(savedFlexEnabled !== null ? savedFlexEnabled === 'true' : legacyShortPosts);
          setSignatureFlex(savedFlexText !== null ? savedFlexText : legacySignature);
          setSignatureCommentsEnabled(savedCommentsEnabled !== null ? savedCommentsEnabled === 'true' : true);
          setSignatureComments(savedCommentsText !== null ? savedCommentsText : legacySignature);
          const savedAutoloadMore = localStorage.getItem(`app-autoload-more-${user.username}`);
          setAutoloadMore(savedAutoloadMore !== 'false');
          const savedNotificationFilterTypes = localStorage.getItem(`app-notification-filter-types-${user.username}`);
          setNotificationFilterTypes(savedNotificationFilterTypes ? JSON.parse(savedNotificationFilterTypes) : getDefaultNotificationFilterTypes());
          setNotificationDisplayVoteWeight(localStorage.getItem(`app-notification-display-vote-weight-${user.username}`) === 'true');
          setNotiflexionsEnabled(localStorage.getItem(`app-notiflexions-enabled-${user.username}`) === 'true');
          setNotiflexionsGroupByPostAction(localStorage.getItem(`app-notiflexions-group-by-post-action-${user.username}`) === 'true');
          setNotiflexionsFromListsEnabled(localStorage.getItem(`app-notiflexions-from-lists-enabled-${user.username}`) === 'true');
          const savedNotiflexionsListIds = localStorage.getItem(`app-notiflexions-list-ids-${user.username}`);
          setNotiflexionsListIds(savedNotiflexionsListIds ? JSON.parse(savedNotiflexionsListIds) : []);
          const savedNotiflexionsListIncludeComments = localStorage.getItem(`app-notiflexions-list-include-comments-${user.username}`);
          setNotiflexionsListIncludeComments(savedNotiflexionsListIncludeComments ? JSON.parse(savedNotiflexionsListIncludeComments) : []);
          const savedNotiflexionsListIncludeShorts = localStorage.getItem(`app-notiflexions-list-include-shorts-${user.username}`);
          setNotiflexionsListIncludeShorts(savedNotiflexionsListIncludeShorts ? JSON.parse(savedNotiflexionsListIncludeShorts) : []);
          setNotiflexionsMutedBehaviorEnabled(localStorage.getItem(`app-notiflexions-muted-behavior-enabled-${user.username}`) === 'true');
          setNotiflexionsMutedBehaviorMode(localStorage.getItem(`app-notiflexions-muted-behavior-mode-${user.username}`) || 'display_muted');
        }

        // load mute list (either from settings payload or separate endpoint)
        // handle mute list merging: server data may be newer but we also want to import any
        // localStorage entries the first time user hits settings after the feature upgrade.
        const localSaved = localStorage.getItem(`app-muted-users-${user.username}`);
        const localList = localSaved ? localSaved.split(',').filter(Boolean).map(u => u.toLowerCase()) : [];

        if (s.mutedUsers && Array.isArray(s.mutedUsers)) {
          let list = s.mutedUsers.map(u => u.toLowerCase());
          // if server list empty but local has values, migrate them to server
          if ((list.length === 0) && localList.length > 0) {
            list = [...new Set(localList)];
            // push each entry to server asynchronously
            list.forEach(u => {
              fetch(`/api/user/${encodeURIComponent(user.username)}/muted`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ target: u })
              }).catch(err => console.error('[SettingsContext] migrate mute failed', err));
            });
          }
          setMutedUsers(list);
          localStorage.setItem(`app-muted-users-${user.username}`, list.join(','));
        } else {
          try {
            const resp2 = await fetch(`/api/user/${encodeURIComponent(user.username)}/muted`);
            if (resp2.ok) {
              const data2 = await resp2.json();
              let list = (data2.muted || []).map(u => u.toLowerCase());
              // if server returned none but local has some, migrate
              if (list.length === 0 && localList.length > 0) {
                list = [...new Set(localList)];
                list.forEach(u => {
                  fetch(`/api/user/${encodeURIComponent(user.username)}/muted`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ target: u })
                  }).catch(err => console.error('[SettingsContext] migrate mute failed', err));
                });
              }
              setMutedUsers(list);
              localStorage.setItem(`app-muted-users-${user.username}`, list.join(','));
            } else {
              throw new Error(`status ${resp2.status}`);
            }
          } catch (err) {
            console.warn(`[SettingsContext] Unable to load muted list from server, falling back to localStorage`, err);
            if (localList.length > 0) {
              setMutedUsers(localList);
            } else {
              setMutedUsers([]);
            }
          }
        }
      } else {
        // Not logged in - use default theme
        setTheme('dimmed');
        setAccentColor('teal');
        setLayoutMode('sidebar');
        setMaincontentMode('normal');
        setNotificationMode('regular');
        setSingleNotifyOnBlockchain(false);
        setCommunitiesViewMode('list');
        setCommentDisplayMode('card');
        setImageDisplayMode('inline');
        setRewardDisplayMode('default');
        setRewardDisplayCurrency('hive');
        setSignatureFlux('');
        setSignatureFluxEnabled(false);
        setSignatureFlex('');
        setSignatureFlexEnabled(false);
        setSignatureComments('');
        setSignatureCommentsEnabled(true);
        setAutoloadMore(true);
        setNotificationFilterTypes(getDefaultNotificationFilterTypes());
        setNotificationDisplayVoteWeight(false);
        setNotiflexionsEnabled(false);
        setNotiflexionsGroupByPostAction(false);
        setNotiflexionsFromListsEnabled(false);
        setNotiflexionsListIds([]);
        setNotiflexionsMutedBehaviorEnabled(false);
        setNotiflexionsMutedBehaviorMode('display_muted');
        setMutedUsers([]);
      }
      setHasUnsavedChanges(false);
    };
    load();
  }, [user?.username]);

  useEffect(() => {
    const themeString = `${theme}-${accentColor}`;
    document.documentElement.setAttribute('data-theme', themeString);
    document.documentElement.setAttribute('data-layout', layoutMode);
    document.documentElement.setAttribute('data-maincontent', maincontentMode);
  }, [theme, accentColor, layoutMode, maincontentMode]);

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    setHasUnsavedChanges(true);
  };

  const updateAccentColor = (newAccent) => {
    setAccentColor(newAccent);
    setHasUnsavedChanges(true);
  };

  const updateLayoutMode = (newLayout) => {
    setLayoutMode(newLayout);
    setHasUnsavedChanges(true);
  };

  const updateMaincontentMode = (newMaincontentMode) => {
    setMaincontentMode(newMaincontentMode);
    setHasUnsavedChanges(true);
  }

  const updateCommunitiesViewMode = (newViewMode) => {
    setCommunitiesViewMode(newViewMode);
    setHasUnsavedChanges(true);
  };

  const updateCommentDisplayMode = (newMode) => {
    setCommentDisplayMode(newMode);
    setHasUnsavedChanges(true);
  };

  const updateImageDisplayMode = (newMode) => {
    setImageDisplayMode(newMode);
    setHasUnsavedChanges(true);
  };

  const updateShowVoteSlider = (val) => {
    setShowVoteSliderSetting(!!val);
    setHasUnsavedChanges(true);
  };

  const updateVoteDefaultWeight = (percent) => {
    const p = Math.max(0, Math.min(100, Number(percent) || 0));
    setVoteDefaultWeight(p);
    setHasUnsavedChanges(true);
  };

  const updateRewardDisplayMode = (mode) => {
    setRewardDisplayMode(mode);
    setHasUnsavedChanges(true);
  };

  const updateRewardDisplayCurrency = (currency) => {
    setRewardDisplayCurrency(currency);
    setHasUnsavedChanges(true);
  };

  const updateSingleNotifyOnBlockchain = (enabled) => {
    setSingleNotifyOnBlockchain(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateSignatureFlux = (newSignature) => {
    setSignatureFlux(newSignature);
    setHasUnsavedChanges(true);
  };

  const updateSignatureFluxEnabled = (enabled) => {
    setSignatureFluxEnabled(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateSignatureFlex = (newSignature) => {
    setSignatureFlex(newSignature);
    setHasUnsavedChanges(true);
  };

  const updateSignatureFlexEnabled = (enabled) => {
    setSignatureFlexEnabled(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateSignatureComments = (newSignature) => {
    setSignatureComments(newSignature);
    setHasUnsavedChanges(true);
  };

  const updateSignatureCommentsEnabled = (enabled) => {
    setSignatureCommentsEnabled(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateShowCommunityPinned = (val) => {
    setShowCommunityPinned(!!val);
    setHasUnsavedChanges(true);
  };

  const updateDefaultBeneficiaries = (tags) => {
    // Intentionally does NOT set hasUnsavedChanges — default beneficiaries
    // are only persisted via handleProfileSave (atomically to proflex + DB).
    setDefaultBeneficiaries(Array.isArray(tags) ? tags : []);
  };

  const updateStartFeed = (val) => {
    const feed = val === 'flux' ? 'flux' : 'flex';
    setStartFeed(feed);
    // Auto-set a sensible default path when switching
    if (feed === 'flux') {
      setStartFeedPath('/articles/all/following');
    } else {
      setStartFeedPath('/feed/flex');
    }
    setHasUnsavedChanges(true);
  };

  const updateStartFeedPath = (path) => {
    setStartFeedPath(path);
    setHasUnsavedChanges(true);
  };

  const updateAutoloadMore = (val) => {
    setAutoloadMore(!!val);
    setHasUnsavedChanges(true);
  };

  const buildSettingsPayload = (overrides = {}) => ({
    ...(user?.settings || {}),
    ...overrides,
    theme: overrides.theme ?? theme,
    accentColor: overrides.accentColor ?? accentColor,
    layoutMode: overrides.layoutMode ?? layoutMode,
    maincontentMode: overrides.maincontentMode ?? maincontentMode,
    notificationMode: overrides.notificationMode ?? notificationMode,
    singleNotifyOnBlockchain: overrides.singleNotifyOnBlockchain ?? singleNotifyOnBlockchain,
    communitiesViewMode: overrides.communitiesViewMode ?? communitiesViewMode,
    commentDisplayMode: overrides.commentDisplayMode ?? commentDisplayMode,
    imageDisplayMode: overrides.imageDisplayMode ?? imageDisplayMode,
    showVoteSlider: overrides.showVoteSlider ?? showVoteSliderSetting,
    voteDefaultWeight: overrides.voteDefaultWeight ?? voteDefaultWeight,
    rewardDisplayMode: overrides.rewardDisplayMode ?? rewardDisplayMode,
    rewardDisplayCurrency: overrides.rewardDisplayCurrency ?? rewardDisplayCurrency,
    signatureFlux: overrides.signatureFlux ?? signatureFlux,
    signatureFluxEnabled: overrides.signatureFluxEnabled ?? signatureFluxEnabled,
    signatureFlex: overrides.signatureFlex ?? signatureFlex,
    signatureFlexEnabled: overrides.signatureFlexEnabled ?? signatureFlexEnabled,
    signatureComments: overrides.signatureComments ?? signatureComments,
    signatureCommentsEnabled: overrides.signatureCommentsEnabled ?? signatureCommentsEnabled,
    showCommunityPinned: overrides.showCommunityPinned ?? showCommunityPinned,
    startFeed: overrides.startFeed ?? startFeed,
    startFeedPath: overrides.startFeedPath ?? startFeedPath,
    autoload_more: overrides.autoload_more ?? autoloadMore,
    notificationFilterTypes: overrides.notificationFilterTypes ?? notificationFilterTypes,
    notificationDisplayVoteWeight: overrides.notificationDisplayVoteWeight ?? notificationDisplayVoteWeight,
    notiflexionsEnabled: overrides.notiflexionsEnabled ?? notiflexionsEnabled,
    notiflexionsGroupByPostAction: overrides.notiflexionsGroupByPostAction ?? notiflexionsGroupByPostAction,
    notiflexionsFromListsEnabled: overrides.notiflexionsFromListsEnabled ?? notiflexionsFromListsEnabled,
    notiflexionsListIds: overrides.notiflexionsListIds ?? notiflexionsListIds,
    notiflexionsListIncludeComments: overrides.notiflexionsListIncludeComments ?? notiflexionsListIncludeComments,
    notiflexionsListIncludeShorts: overrides.notiflexionsListIncludeShorts ?? notiflexionsListIncludeShorts,
    notiflexionsMutedBehaviorEnabled: overrides.notiflexionsMutedBehaviorEnabled ?? notiflexionsMutedBehaviorEnabled,
    notiflexionsMutedBehaviorMode: overrides.notiflexionsMutedBehaviorMode ?? notiflexionsMutedBehaviorMode,
    // defaultBeneficiaries is intentionally excluded — it must only be saved
    // atomically via handleProfileSave (proflex + DB together).
  });

  const saveSettings = async (overrides = {}) => {
    if (!user?.username) return false;

    const payload = buildSettingsPayload(overrides);

    if (overrides.autovote_global_pause !== undefined) {
      setAutovoteGlobalPause(overrides.autovote_global_pause === true || overrides.autovote_global_pause === 'true');
    }
    if (overrides.autovote_advanced_global_pause !== undefined) {
      setAutovoteAdvancedGlobalPause(overrides.autovote_advanced_global_pause === true || overrides.autovote_advanced_global_pause === 'true');
    }
    if (overrides.autovote_mana_limit !== undefined) {
      setAutovoteManaLimit(parseInt(overrides.autovote_mana_limit, 10) || 0);
    }
    if (overrides.autovote_advanced_mana_limit !== undefined) {
      setAutovoteAdvancedManaLimit(parseInt(overrides.autovote_advanced_mana_limit, 10) || 0);
    }
    if (overrides.autovote_allow_self_vote !== undefined) {
      setAutovoteAllowSelfVote(overrides.autovote_allow_self_vote === true || overrides.autovote_allow_self_vote === 'true');
    }

    // Persist local settings immediately
    localStorage.setItem(`app-theme-${user.username}`, payload.theme);
    localStorage.setItem(`app-accent-${user.username}`, payload.accentColor);
    localStorage.setItem(`app-layout-${user.username}`, payload.layoutMode);
    localStorage.setItem(`app-communities-view-${user.username}`, payload.communitiesViewMode);
    localStorage.setItem(`app-image-display-${user.username}`, payload.imageDisplayMode);
    localStorage.setItem(`app-show-vote-slider-${user.username}`, String(payload.showVoteSlider));
    localStorage.setItem(`app-vote-default-weight-${user.username}`, String(payload.voteDefaultWeight));
    localStorage.setItem(`app-maincontent-${user.username}`, payload.maincontentMode);
    localStorage.setItem(`app-reward-display-${user.username}`, payload.rewardDisplayMode);
    localStorage.setItem(`app-reward-currency-${user.username}`, payload.rewardDisplayCurrency);
    localStorage.setItem(`app-signature-flux-${user.username}`, payload.signatureFlux || '');
    localStorage.setItem(`app-signature-flux-enabled-${user.username}`, String(payload.signatureFluxEnabled));
    localStorage.setItem(`app-signature-flex-${user.username}`, payload.signatureFlex || '');
    localStorage.setItem(`app-signature-flex-enabled-${user.username}`, String(payload.signatureFlexEnabled));
    localStorage.setItem(`app-signature-comments-${user.username}`, payload.signatureComments || '');
    localStorage.setItem(`app-signature-comments-enabled-${user.username}`, String(payload.signatureCommentsEnabled));
    localStorage.setItem(`app-show-community-pinned-${user.username}`, String(payload.showCommunityPinned));
    localStorage.setItem(`app-autoload-more-${user.username}`, String(payload.autoload_more));
    localStorage.setItem(`app-single-notify-chain-${user.username}`, String(payload.singleNotifyOnBlockchain));
    localStorage.setItem(`app-notification-filter-types-${user.username}`, JSON.stringify(payload.notificationFilterTypes || getDefaultNotificationFilterTypes()));
    localStorage.setItem(`app-notification-display-vote-weight-${user.username}`, String(payload.notificationDisplayVoteWeight));
    localStorage.setItem(`app-notiflexions-enabled-${user.username}`, String(payload.notiflexionsEnabled));
    localStorage.setItem(`app-notiflexions-group-by-post-action-${user.username}`, String(payload.notiflexionsGroupByPostAction));
    localStorage.setItem(`app-notiflexions-from-lists-${user.username}`, String(payload.notiflexionsFromListsEnabled));
    localStorage.setItem(`app-notiflexions-from-lists-enabled-${user.username}`, String(payload.notiflexionsFromListsEnabled));
    localStorage.setItem(`app-notiflexions-list-ids-${user.username}`, JSON.stringify(payload.notiflexionsListIds || []));
    localStorage.setItem(`app-notiflexions-list-include-comments-${user.username}`, JSON.stringify(payload.notiflexionsListIncludeComments || []));
    localStorage.setItem(`app-notiflexions-list-include-shorts-${user.username}`, JSON.stringify(payload.notiflexionsListIncludeShorts || []));
    localStorage.setItem(`app-notiflexions-muted-behavior-enabled-${user.username}`, String(payload.notiflexionsMutedBehaviorEnabled));
    localStorage.setItem(`app-notiflexions-muted-behavior-mode-${user.username}`, String(payload.notiflexionsMutedBehaviorMode || 'display_muted'));
    // Persist to server
    try {
      await fetch(`/api/user/${encodeURIComponent(user.username)}/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error('Failed to save settings server-side', err);
    }

    // Clear unsaved flag - we've accepted local changes
    markHasUnsavedChanges(false);
    return true;
  };

  const updateNotificationFilterTypes = (types) => {
    setNotificationFilterTypes(Array.isArray(types) ? types : getDefaultNotificationFilterTypes());
    setHasUnsavedChanges(true);
  };

  const updateNotificationDisplayVoteWeight = (enabled) => {
    setNotificationDisplayVoteWeight(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateNotiflexionsEnabled = (enabled) => {
    setNotiflexionsEnabled(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateNotiflexionsGroupByPostAction = (enabled) => {
    setNotiflexionsGroupByPostAction(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateNotiflexionsFromListsEnabled = (enabled) => {
    setNotiflexionsFromListsEnabled(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateNotiflexionsListIds = (ids) => {
    setNotiflexionsListIds(Array.isArray(ids) ? ids : []);
    setHasUnsavedChanges(true);
  };

  const updateNotiflexionsListIncludeComments = (ids) => {
    setNotiflexionsListIncludeComments(Array.isArray(ids) ? ids : []);
    setHasUnsavedChanges(true);
  };

  const updateNotiflexionsListIncludeShorts = (ids) => {
    setNotiflexionsListIncludeShorts(Array.isArray(ids) ? ids : []);
    setHasUnsavedChanges(true);
  };

  const updateNotiflexionsMutedBehaviorEnabled = (enabled) => {
    setNotiflexionsMutedBehaviorEnabled(!!enabled);
    setHasUnsavedChanges(true);
  };

  const updateNotiflexionsMutedBehaviorMode = (mode) => {
    setNotiflexionsMutedBehaviorMode(mode || 'display_muted');
    setHasUnsavedChanges(true);
  };

  // mute/unmute helpers
  const isUserMuted = (username) => {
    if (!username) return false;
    return mutedUsers.includes(username.toLowerCase());
  };

  const addMutedUser = async (username) => {
    if (!username || !user?.username) return;
    const u = username.toLowerCase();
    if (mutedUsers.includes(u)) return;
    // optimistic update locally
    const updated = [...mutedUsers, u];
    setMutedUsers(updated);
    localStorage.setItem(`app-muted-users-${user.username}`, updated.join(','));

    try {
      await fetch(`/api/user/${encodeURIComponent(user.username)}/muted`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: u })
      });
    } catch (err) {
      console.error('[SettingsContext] Error adding muted user to server', err);
      // ignore; server is eventually consistent
    }
  };

  const removeMutedUser = async (username) => {
    if (!username || !user?.username) return;
    const u = username.toLowerCase();
    const updated = mutedUsers.filter(x => x !== u);
    setMutedUsers(updated);
    localStorage.setItem(`app-muted-users-${user.username}`, updated.join(','));

    try {
      await fetch(`/api/user/${encodeURIComponent(user.username)}/muted/${encodeURIComponent(u)}`, {
        method: 'DELETE'
      });
    } catch (err) {
      console.error('[SettingsContext] Error removing muted user from server', err);
    }
  };
  const markHasUnsavedChanges = (val) => {
    setHasUnsavedChanges(val);
  };

  const resetSettings = () => {
    if (user?.username) {
      const savedTheme = localStorage.getItem(`app-theme-${user.username}`) || 'dark';
      const savedAccent = localStorage.getItem(`app-accent-${user.username}`) || 'blue';
      const savedLayout = localStorage.getItem(`app-layout-${user.username}`) || 'sidebar';
      const savedMaincontent = localStorage.getItem(`app-maincontent-${user.username}`) || 'normal';
      const savedCommunitiesView = localStorage.getItem(`app-communities-view-${user.username}`) || 'list';
      const savedCommentDisplay = localStorage.getItem(`app-comment-display-${user.username}`) || 'card';
      const savedImageDisplay = localStorage.getItem(`app-image-display-${user.username}`) || 'inline';
      const savedSingleNotifyOnBlockchain = localStorage.getItem(`app-single-notify-chain-${user.username}`);
      const savedRewardDisplay = localStorage.getItem(`app-reward-display-${user.username}`) || 'default';
      const savedRewardCurrency = localStorage.getItem(`app-reward-currency-${user.username}`) || 'hive';
      const savedFluxEnabled = localStorage.getItem(`app-signature-flux-enabled-${user.username}`);
      const savedFluxText = localStorage.getItem(`app-signature-flux-${user.username}`);
      const savedFlexEnabled = localStorage.getItem(`app-signature-flex-enabled-${user.username}`);
      const savedFlexText = localStorage.getItem(`app-signature-flex-${user.username}`);
      const savedCommentsEnabled = localStorage.getItem(`app-signature-comments-enabled-${user.username}`);
      const savedCommentsText = localStorage.getItem(`app-signature-comments-${user.username}`);

      // fallbacks
      const legacySignature = localStorage.getItem(`app-signature-${user.username}`) || '';
      const legacyShortPosts = localStorage.getItem(`app-signature-short-posts-${user.username}`) === 'true';
      const legacyArticles = localStorage.getItem(`app-signature-articles-${user.username}`) === 'true';
      const savedAutoloadMore = localStorage.getItem(`app-autoload-more-${user.username}`);
      const savedNotificationFilterTypes = localStorage.getItem(`app-notification-filter-types-${user.username}`);
      const savedNotiflexionsListIds = localStorage.getItem(`app-notiflexions-list-ids-${user.username}`);
      const savedNotiflexionsListIncludeComments = localStorage.getItem(`app-notiflexions-list-include-comments-${user.username}`);
      const savedNotiflexionsListIncludeShorts = localStorage.getItem(`app-notiflexions-list-include-shorts-${user.username}`);
      setTheme(savedTheme);
      setAccentColor(savedAccent);
      setLayoutMode(savedLayout);
      setMaincontentMode(savedMaincontent);
      setCommunitiesViewMode(savedCommunitiesView);
      setCommentDisplayMode(savedCommentDisplay);
      setImageDisplayMode(savedImageDisplay);
      setSingleNotifyOnBlockchain(savedSingleNotifyOnBlockchain === 'true');
      setRewardDisplayMode(savedRewardDisplay);
      setRewardDisplayCurrency(savedRewardCurrency);
      setSignatureFluxEnabled(savedFluxEnabled !== null ? savedFluxEnabled === 'true' : legacyArticles);
      setSignatureFlux(savedFluxText !== null ? savedFluxText : legacySignature);
      setSignatureFlexEnabled(savedFlexEnabled !== null ? savedFlexEnabled === 'true' : legacyShortPosts);
      setSignatureFlex(savedFlexText !== null ? savedFlexText : legacySignature);
      setSignatureCommentsEnabled(savedCommentsEnabled !== null ? savedCommentsEnabled === 'true' : true);
      setSignatureComments(savedCommentsText !== null ? savedCommentsText : legacySignature);
      setAutoloadMore(savedAutoloadMore !== 'false');
      setNotificationFilterTypes(savedNotificationFilterTypes ? JSON.parse(savedNotificationFilterTypes) : getDefaultNotificationFilterTypes());
      setNotificationDisplayVoteWeight(localStorage.getItem(`app-notification-display-vote-weight-${user.username}`) === 'true');
      setNotiflexionsEnabled(localStorage.getItem(`app-notiflexions-enabled-${user.username}`) === 'true');
      setNotiflexionsGroupByPostAction(localStorage.getItem(`app-notiflexions-group-by-post-action-${user.username}`) === 'true');
      setNotiflexionsFromListsEnabled(localStorage.getItem(`app-notiflexions-from-lists-enabled-${user.username}`) === 'true');
      setNotiflexionsListIds(savedNotiflexionsListIds ? JSON.parse(savedNotiflexionsListIds) : []);
      setNotiflexionsListIncludeComments(savedNotiflexionsListIncludeComments ? JSON.parse(savedNotiflexionsListIncludeComments) : []);
      setNotiflexionsListIncludeShorts(savedNotiflexionsListIncludeShorts ? JSON.parse(savedNotiflexionsListIncludeShorts) : []);
      setNotiflexionsMutedBehaviorEnabled(localStorage.getItem(`app-notiflexions-muted-behavior-enabled-${user.username}`) === 'true');
      setNotiflexionsMutedBehaviorMode(localStorage.getItem(`app-notiflexions-muted-behavior-mode-${user.username}`) || 'display_muted');
      setHasUnsavedChanges(false);
    }
  };

  const value = {
    theme,
    accentColor,
    layoutMode,
    maincontentMode,
    communitiesViewMode,
    commentDisplayMode,
    imageDisplayMode,
    showVoteSlider: showVoteSliderSetting,
    voteDefaultWeight,
    rewardDisplayMode,
    rewardDisplayCurrency,
    notificationMode,
    singleNotifyOnBlockchain,
    hasUnsavedChanges,
    mutedUsers,
    isUserMuted,
    addMutedUser,
    removeMutedUser,
    updateTheme,
    updateAccentColor,
    updateLayoutMode,
    updateMaincontentMode,
    updateCommunitiesViewMode,
    updateCommentDisplayMode,
    updateImageDisplayMode,
    updateShowVoteSlider,
    updateVoteDefaultWeight,
    updateRewardDisplayMode,
    updateRewardDisplayCurrency,
    updateSingleNotifyOnBlockchain,
    updateSignatureFlux,
    updateSignatureFluxEnabled,
    updateSignatureFlex,
    updateSignatureFlexEnabled,
    updateSignatureComments,
    updateSignatureCommentsEnabled,
    updateShowCommunityPinned,
    updateDefaultBeneficiaries,
    updateStartFeed,
    updateStartFeedPath,
    autoload_more: autoloadMore,
    updateAutoloadMore,
    signatureFlux,
    signatureFluxEnabled,
    signatureFlex,
    signatureFlexEnabled,
    signatureComments,
    signatureCommentsEnabled,
    showCommunityPinned,
    defaultBeneficiaries,
    startFeed,
    startFeedPath,
    autovoteGlobalPause,
    autovoteAdvancedGlobalPause,
    autovoteManaLimit,
    autovoteAdvancedManaLimit,
    autovoteAllowSelfVote,
    notificationFilterTypes,
    notificationDisplayVoteWeight,
    notiflexionsEnabled,
    notiflexionsGroupByPostAction,
    notiflexionsFromListsEnabled,
    notiflexionsListIds,
    notiflexionsListIncludeComments,
    notiflexionsListIncludeShorts,
    notiflexionsMutedBehaviorEnabled,
    notiflexionsMutedBehaviorMode,
    saveSettings,
    resetSettings,
    markHasUnsavedChanges,
    updateNotificationFilterTypes,
    updateNotificationDisplayVoteWeight,
    updateNotiflexionsEnabled,
    updateNotiflexionsGroupByPostAction,
    updateNotiflexionsFromListsEnabled,
    updateNotiflexionsListIds,
    updateNotiflexionsListIncludeComments,
    updateNotiflexionsListIncludeShorts,
    updateNotiflexionsMutedBehaviorEnabled,
    updateNotiflexionsMutedBehaviorMode,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
